import React, { useState } from 'react';

// Interfața pentru un eveniment
interface Event {
  id: number;
  titlu: string;
  data: string;
  ora: string;
  locatie: string;
  descriere: string;
  imagine: string;
  nivelMinimParticipare: 'Liber' | 'Bronz' | 'Argint' | 'Aur' | 'Platină';
  participanti: number;
  statut: 'trecut' | 'viitor';
  categorie: string;
  invitatiSpeciali: {
    nume: string;
    functie: string;
    companie: string;
    avatar?: string;
  }[];
  agenda: {
    ora: string;
    titlu: string;
    descriere?: string;
    speaker?: string;
  }[];
}

// Interfața pentru detaliile unui eveniment
interface EventDetailProps {
  event: Event;
  onBack: () => void;
}

// Componenta de detalii eveniment
const EventDetail: React.FC<EventDetailProps> = ({ event, onBack }) => {
  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header cu navigare înapoi */}
      <div className="p-4 border-b flex items-center">
        <button 
          onClick={onBack}
          className="mr-3 text-gray-600 hover:text-blue-600"
        >
          ← Înapoi
        </button>
        <h1 className="text-xl font-semibold">Detalii Eveniment</h1>
      </div>

      <div className="p-4">
        {/* Imagine header */}
        <div className="relative rounded-lg overflow-hidden mb-6">
          <img 
            src={event.imagine} 
            alt={event.titlu} 
            className="w-full h-64 object-cover"
          />
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-6">
            <h2 className="text-white text-2xl font-bold">{event.titlu}</h2>
          </div>
        </div>

        {/* Informații principale */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-gray-600 mb-1">Data și ora</div>
            <div className="font-semibold">{event.data}, {event.ora}</div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-gray-600 mb-1">Locație</div>
            <div className="font-semibold">{event.locatie}</div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-gray-600 mb-1">Nivel minim de participare</div>
            <div className={`font-semibold ${
              event.nivelMinimParticipare === 'Liber' ? 'text-green-600' :
              event.nivelMinimParticipare === 'Bronz' ? 'text-yellow-800' : 
              event.nivelMinimParticipare === 'Argint' ? 'text-gray-600' : 
              event.nivelMinimParticipare === 'Aur' ? 'text-yellow-600' : 'text-purple-600'
            }`}>
              {event.nivelMinimParticipare}
            </div>
          </div>
        </div>

        {/* Descriere */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-3">Despre eveniment</h3>
          <div className="text-gray-700 whitespace-pre-line">{event.descriere}</div>
        </div>

        {/* Invitați speciali */}
        {event.invitatiSpeciali.length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-3">Invitați speciali</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {event.invitatiSpeciali.map((invitat, index) => (
                <div key={index} className="flex items-center p-3 border rounded-lg">
                  <div className="w-12 h-12 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center font-bold mr-3">
                    {invitat.avatar || invitat.nume.substring(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <div className="font-semibold">{invitat.nume}</div>
                    <div className="text-sm text-gray-600">{invitat.functie}, {invitat.companie}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Agenda */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-3">Agenda evenimentului</h3>
          <div className="space-y-4">
            {event.agenda.map((item, index) => (
              <div key={index} className="border-l-4 border-blue-500 pl-4 py-2">
                <div className="text-blue-600 font-semibold">{item.ora}</div>
                <div className="font-semibold mt-1">{item.titlu}</div>
                {item.descriere && <div className="text-gray-600 mt-1">{item.descriere}</div>}
                {item.speaker && <div className="text-sm text-gray-600 mt-1">Speaker: {item.speaker}</div>}
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        {event.statut === 'viitor' && (
          <div className="flex flex-col md:flex-row items-center justify-between bg-blue-50 p-6 rounded-lg">
            <div className="mb-4 md:mb-0 md:mr-6">
              <h4 className="font-semibold mb-1">Dorești să participi la acest eveniment?</h4>
              <p className="text-gray-600">Înregistrează-te acum pentru a-ți rezerva locul!</p>
            </div>
            <button className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md">
              Înregistrează-te
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Componenta principală Events
const Events: React.FC = () => {
  // State pentru filtrare și eveniment selectat
  const [filter, setFilter] = useState<'toate' | 'viitoare' | 'trecute'>('toate');
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  
  // Date exemplu pentru evenimente
  const events: Event[] = [
    {
      id: 1,
      titlu: "Conferința Anuală de Inovație în Afaceri",
      data: "15 Mai 2025",
      ora: "10:00 - 18:00",
      locatie: "Hotel Unirea, Sala Mihai Eminescu, Focșani",
      descriere: "Evenimentul anual de referință pentru antreprenorii din Vrancea. Conferința va reuni lideri de afaceri, investitori și experți care vor împărtăși cele mai noi tendințe și strategii pentru inovație și sustenabilitate în afaceri.\n\nÎn cadrul conferinței vor avea loc prezentări, dezbateri și sesiuni interactive pe teme precum: transformarea digitală, managementul resurselor în perioade de criză, fonduri europene pentru IMM-uri și multe altele.\n\nVei avea oportunitatea de a întâlni antreprenori de succes și de a dezvolta conexiuni valoroase care îți pot impulsiona afacerea.",
      imagine: "/2.png",
      nivelMinimParticipare: 'Argint',
      participanti: 120,
      statut: 'viitor',
      categorie: 'Conferință',
      invitatiSpeciali: [
        {
          nume: "Alexandru Dumitrescu",
          functie: "Director General",
          companie: "Invest Romania",
          avatar: "AD"
        },
        {
          nume: "Elena Popovici",
          functie: "Expert Fonduri Europene",
          companie: "Ministerul Economiei",
          avatar: "EP"
        },
        {
          nume: "Mihai Ionescu",
          functie: "CEO",
          companie: "TechVision Group",
          avatar: "MI"
        },
        {
          nume: "Laura Georgescu",
          functie: "Fondator",
          companie: "Sustainable Business Network",
          avatar: "LG"
        }
      ],
      agenda: [
        {
          ora: "09:30 - 10:00",
          titlu: "Înregistrarea participanților și cafea de bun venit"
        },
        {
          ora: "10:00 - 10:30",
          titlu: "Deschiderea oficială a conferinței",
          descriere: "Cuvânt de bun venit și prezentarea obiectivelor conferinței",
          speaker: "Maria Popescu, Președinte Patronat Vrancea"
        },
        {
          ora: "10:30 - 11:30",
          titlu: "Inovație și sustenabilitate: Direcții strategice pentru antreprenorii români",
          speaker: "Alexandru Dumitrescu, Director General Invest Romania"
        },
        {
          ora: "11:30 - 12:30",
          titlu: "Accesarea fondurilor europene pentru inovare și dezvoltare",
          speaker: "Elena Popovici, Expert Fonduri Europene"
        },
        {
          ora: "12:30 - 13:30",
          titlu: "Pauză de prânz și networking"
        },
        {
          ora: "13:30 - 14:30",
          titlu: "Transformarea digitală a afacerilor tradiționale",
          speaker: "Mihai Ionescu, CEO TechVision Group"
        },
        {
          ora: "14:30 - 15:30",
          titlu: "Panel: Provocări și oportunități pentru antreprenorii din Vrancea",
          descriere: "Discuție deschisă cu antreprenori locali de succes"
        },
        {
          ora: "15:30 - 16:00",
          titlu: "Pauză de cafea"
        },
        {
          ora: "16:00 - 17:00",
          titlu: "Workshop: Strategii de marketing pentru afacerile locale",
          speaker: "Laura Georgescu, Fondator Sustainable Business Network"
        },
        {
          ora: "17:00 - 18:00",
          titlu: "Sesiune de networking și închiderea evenimentului"
        }
      ]
    },
    {
      id: 2,
      titlu: "Workshop: Digitalizarea afacerii tale",
      data: "28 Aprilie 2025",
      ora: "14:00 - 18:00",
      locatie: "Centrul de Afaceri Vrancea, Sala Training",
      descriere: "Workshop intensiv dedicat antreprenorilor care doresc să implementeze soluții digitale pentru eficientizarea afacerilor lor. Vei învăța despre instrumente digitale practice, strategii de marketing online și metode de optimizare a operațiunilor folosind tehnologia.\n\nAcest workshop interactiv îți va oferi cunoștințele necesare pentru a face primii pași spre digitalizarea afacerii tale, indiferent de industrie sau dimensiunea companiei.",
      imagine: "/2.png",
      nivelMinimParticipare: 'Bronz',
      participanti: 30,
      statut: 'viitor',
      categorie: 'Workshop',
      invitatiSpeciali: [
        {
          nume: "Cristian Mureșan",
          functie: "Specialist Marketing Digital",
          companie: "Digital Growth Agency",
          avatar: "CM"
        },
        {
          nume: "Andreea Popa",
          functie: "Expert ERP & CRM",
          companie: "Business Systems",
          avatar: "AP"
        }
      ],
      agenda: [
        {
          ora: "13:30 - 14:00",
          titlu: "Înregistrarea participanților"
        },
        {
          ora: "14:00 - 14:30",
          titlu: "Introducere în digitalizarea afacerilor",
          speaker: "Ion Marinescu, Coordonator Program Digitalizare Patronat Vrancea"
        },
        {
          ora: "14:30 - 15:30",
          titlu: "Strategii de marketing digital pentru afaceri locale",
          speaker: "Cristian Mureșan, Specialist Marketing Digital"
        },
        {
          ora: "15:30 - 16:00",
          titlu: "Pauză de cafea și networking"
        },
        {
          ora: "16:00 - 17:00",
          titlu: "Implementarea sistemelor ERP și CRM în IMM-uri",
          speaker: "Andreea Popa, Expert ERP & CRM"
        },
        {
          ora: "17:00 - 18:00",
          titlu: "Sesiune practică: Evaluarea nevoilor digitale ale afacerii tale",
          descriere: "Workshop interactiv cu feedback personalizat"
        }
      ]
    },
    {
      id: 3,
      titlu: "Gala Antreprenorilor Vrânceni",
      data: "10 Iunie 2025",
      ora: "19:00 - 23:00",
      locatie: "Hotel Laguna, Sala de Evenimente, Focșani",
      descriere: "Eveniment de prestigiu dedicat recunoașterii și premierii celor mai inovativi și performanți antreprenori din județul Vrancea. În cadrul galei vor fi acordate premii pentru excelență în afaceri în diverse categorii.\n\nParticiparea la această gală este exclusivă, fiind rezervată membrilor cu nivel Aur și Platină din comunitatea Patronat Vrancea, precum și invitaților speciali.\n\nVa fi o seară elegantă cu cină festivă, momente artistice și oportunități de networking de înalt nivel.",
      imagine: "/2.png",
      nivelMinimParticipare: 'Aur',
      participanti: 80,
      statut: 'viitor',
      categorie: 'Gală',
      invitatiSpeciali: [
        {
          nume: "Nicolae Popescu",
          functie: "Secretar de Stat",
          companie: "Ministerul Economiei",
          avatar: "NP"
        },
        {
          nume: "Gabriela Ionescu",
          functie: "Președinte",
          companie: "Camera de Comerț și Industrie a României",
          avatar: "GI"
        }
      ],
      agenda: [
        {
          ora: "19:00 - 19:30",
          titlu: "Primirea invitaților"
        },
        {
          ora: "19:30 - 20:00",
          titlu: "Deschiderea oficială a Galei",
          speaker: "Maria Popescu, Președinte Patronat Vrancea"
        },
        {
          ora: "20:00 - 20:30",
          titlu: "Cuvânt invitat special",
          speaker: "Nicolae Popescu, Secretar de Stat"
        },
        {
          ora: "20:30 - 21:30",
          titlu: "Cină festivă"
        },
        {
          ora: "21:30 - 22:30",
          titlu: "Ceremonia de premiere",
          descriere: "Acordarea premiilor pentru excelență în afaceri"
        },
        {
          ora: "22:30 - 23:00",
          titlu: "Moment artistic și închiderea oficială"
        }
      ]
    },
    {
      id: 4,
      titlu: "Seminar: Oportunități de finanțare pentru IMM-uri în 2025",
      data: "20 Martie 2025",
      ora: "15:00 - 18:00",
      locatie: "Camera de Comerț Vrancea, Sala Conferințe",
      descriere: "Seminar informativ despre programele de finanțare disponibile pentru IMM-uri în anul 2025. Au fost prezentate granturi europene, programe guvernamentale și alte surse de finanțare relevante pentru antreprenorii locali.\n\nParticipanții au avut ocazia să afle detalii concrete despre criteriile de eligibilitate, procesul de aplicare și sfaturi practice pentru maximizarea șanselor de obținere a finanțărilor.",
      imagine: "/2.png",
      nivelMinimParticipare: 'Liber',
      participanti: 65,
      statut: 'trecut',
      categorie: 'Seminar',
      invitatiSpeciali: [
        {
          nume: "Andrei Georgescu",
          functie: "Director Programe",
          companie: "Agenția pentru IMM-uri",
          avatar: "AG"
        }
      ],
      agenda: [
        {
          ora: "14:30 - 15:00",
          titlu: "Înregistrarea participanților"
        },
        {
          ora: "15:00 - 15:30",
          titlu: "Prezentare generală a programelor de finanțare 2025",
          speaker: "Elena Vasilescu, Patron Vrancea"
        },
        {
          ora: "15:30 - 16:30",
          titlu: "Programele guvernamentale pentru IMM-uri",
          speaker: "Andrei Georgescu, Director Programe Agenția pentru IMM-uri"
        },
        {
          ora: "16:30 - 17:00",
          titlu: "Pauză de cafea"
        },
        {
          ora: "17:00 - 18:00",
          titlu: "Sesiune Q&A și consultanță individuală"
        }
      ]
    },
    {
      id: 5,
      titlu: "Business Networking: Conectăm antreprenorii din Vrancea",
      data: "15 Februarie 2025",
      ora: "18:00 - 21:00",
      locatie: "Axa Business Center, Focșani",
      descriere: "Eveniment dedicat conectării antreprenorilor din diverse industrii din județul Vrancea. Participanții au avut ocazia să își prezinte afacerile, să stabilească noi conexiuni profesionale și să exploreze oportunități de colaborare.\n\nFormatul evenimentului a inclus sesiuni de speed networking, prezentări scurte de tip pitch și timp alocat pentru conversații informale într-o atmosferă relaxată.",
      imagine: "/2.png",
      nivelMinimParticipare: 'Bronz',
      participanti: 45,
      statut: 'trecut',
      categorie: 'Networking',
      invitatiSpeciali: [],
      agenda: [
        {
          ora: "18:00 - 18:30",
          titlu: "Primirea invitaților și welcome drink"
        },
        {
          ora: "18:30 - 19:00",
          titlu: "Introducere și prezentarea formatului evenimentului",
          speaker: "Adrian Munteanu, Coordonator evenimente Patronat Vrancea"
        },
        {
          ora: "19:00 - 20:00",
          titlu: "Sesiune speed networking"
        },
        {
          ora: "20:00 - 20:30",
          titlu: "Prezentări de tip pitch - participanți selectați"
        },
        {
          ora: "20:30 - 21:00",
          titlu: "Networking liber și închiderea evenimentului"
        }
      ]
    }
  ];

  // Filtrăm evenimentele în funcție de selecție
  const filteredEvents = events.filter(event => {
    if (filter === 'toate') return true;
    return event.statut === (filter === 'viitoare' ? 'viitor' : 'trecut');
  });

  // Dacă este selectat un eveniment, afișăm detaliile acestuia
  if (selectedEvent) {
    return <EventDetail event={selectedEvent} onBack={() => setSelectedEvent(null)} />;
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-4 border-b">
        <h2 className="text-xl font-semibold">Evenimente</h2>
        <p className="text-gray-600">Descoperă evenimentele organizate de Patronatul Vrancea</p>
      </div>

      {/* Filtre */}
      <div className="p-4 border-b">
        <div className="flex space-x-4">
          <button
            className={`px-4 py-2 rounded-md font-medium ${filter === 'toate' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            onClick={() => setFilter('toate')}
          >
            Toate evenimentele
          </button>
          <button
            className={`px-4 py-2 rounded-md font-medium ${filter === 'viitoare' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            onClick={() => setFilter('viitoare')}
          >
            Evenimente viitoare
          </button>
          <button
            className={`px-4 py-2 rounded-md font-medium ${filter === 'trecute' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            onClick={() => setFilter('trecute')}
          >
            Evenimente trecute
          </button>
        </div>
      </div>

      {/* Secțiunea de evenimente viitoare */}
      {filteredEvents.filter(e => e.statut === 'viitor').length > 0 && (
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold mb-4">Următoarele evenimente</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filteredEvents
              .filter(e => e.statut === 'viitor')
              .sort((a, b) => new Date(a.data.split(' ').reverse().join('-')).getTime() - new Date(b.data.split(' ').reverse().join('-')).getTime())
              .map(event => (
                <div
                  key={event.id}
                  className="border rounded-lg overflow-hidden hover:shadow-md transition-all cursor-pointer"
                  onClick={() => setSelectedEvent(event)}
                >
                  <div className="relative">
                    <img src={event.imagine} alt={event.titlu} className="w-full h-48 object-cover" />
                    <div className="absolute top-0 right-0 bg-blue-600 text-white p-2 m-2 rounded-md">
                      <div className="text-xs">Nivel minim:</div>
                      <div className="font-semibold text-sm">{event.nivelMinimParticipare}</div>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="text-lg font-semibold text-blue-600">{event.titlu}</h4>
                      <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">{event.categorie}</span>
                    </div>
                    <div className="flex items-center text-gray-600 mb-2">
                      <span className="mr-2">📅</span>
                      <span>{event.data}, {event.ora}</span>
                    </div>
                    <div className="flex items-center text-gray-600 mb-3">
                      <span className="mr-2">📍</span>
                      <span>{event.locatie}</span>
                    </div>
                    <p className="text-gray-700 mb-4 line-clamp-2">{event.descriere}</p>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">{event.participanti} participanți înscriși</span>
                      <button className="text-blue-600 hover:text-blue-800 font-medium text-sm">
                        Detalii →
                      </button>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Secțiunea de evenimente trecute */}
      {filteredEvents.filter(e => e.statut === 'trecut').length > 0 && (
        <div className="p-4">
          <h3 className="text-lg font-semibold mb-4">Evenimente trecute</h3>
          <div className="grid grid-cols-1 gap-4">
            {filteredEvents
              .filter(e => e.statut === 'trecut')
              .sort((a, b) => new Date(b.data.split(' ').reverse().join('-')).getTime() - new Date(a.data.split(' ').reverse().join('-')).getTime())
              .map(event => (
                <div
                  key={event.id}
                  className="border rounded-lg overflow-hidden hover:shadow-md transition-all cursor-pointer"
                  onClick={() => setSelectedEvent(event)}
                >
                  <div className="flex flex-col md:flex-row">
                    <div className="md:w-1/4">
                      <img src={event.imagine} alt={event.titlu} className="w-full h-full object-cover" />
                    </div>
                    <div className="p-4 md:w-3/4">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="text-lg font-semibold text-blue-600">{event.titlu}</h4>
                        <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full">{event.categorie}</span>
                      </div>
                      <div className="flex items-center text-gray-600 mb-2">
                        <span className="mr-2">📅</span>
                        <span>{event.data}, {event.ora}</span>
                      </div>
                      <div className="flex items-center text-gray-600 mb-3">
                        <span className="mr-2">📍</span>
                        <span>{event.locatie}</span>
                      </div>
                      <p className="text-gray-700 mb-2 line-clamp-2">{event.descriere}</p>
                      <div className="flex justify-between items-center">
                        <div className="flex items-center">
                          <span className="text-sm text-gray-500 mr-3">Nivel minim: {event.nivelMinimParticipare}</span>
                          <span className="text-sm text-gray-500">{event.participanti} participanți</span>
                        </div>
                        <button className="text-blue-600 hover:text-blue-800 font-medium text-sm">
                          Detalii →
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* CTA pentru organizatori de evenimente */}
      <div className="p-6 bg-gray-50 border-t">
        <div className="bg-white p-6 rounded-lg border shadow-sm">
          <h3 className="text-lg font-semibold mb-2">Dorești să organizezi un eveniment?</h3>
          <p className="text-gray-600 mb-4">
            Membrii Patronatului Vrancea pot propune și organiza evenimente pentru comunitatea antreprenorială. 
            Completează formularul și echipa noastră te va contacta pentru detalii.
          </p>
          <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium">
            Propune un eveniment
          </button>
        </div>
      </div>
    </div>
  );
};

export default Events;